import {
  useEffect,
  useRef,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Avatar, Badge, Dot, Tooltip } from './primitives';
import { Menu, type MenuItem } from './overlays';
import { IconOffline, IconRefresh } from './icons';

/**
 * Chrome commun aux deux applications : barre latérale, en-tête, barre
 * d'onglets, filigrane.
 *
 * La conception de ce module vient de la PR #76 (branche
 * `cursor/ui-redesign-wave-0-1-1f46`) : c'est le seul endroit où cette
 * proposition allait plus loin que la mienne, et il valait mieux la reprendre
 * que la refaire. Le portage l'adapte à l'état actuel de la branche — le logo
 * propre à la section, les outils d'en-tête et le nom lu dans les réglages
 * ayant changé entre-temps.
 *
 * Comme pour les écrans hors session, le module ne connaît pas React Router :
 * le composant de lien est injecté. C'est ce qui permet à `packages/ui` de ne
 * dépendre que de React.
 */

/** Lien de navigation fourni par l'application (en pratique, `NavLink`). */
export type AppShellLinkProps = {
  to: string;
  end?: boolean;
  className?: string;
  children: ReactNode;
};

export type AppShellNavItem = {
  to: string;
  label: string;
  short: string;
  icon: ReactNode;
  /** Correspond au `end` de `NavLink` — l'accueil ne doit correspondre qu'exactement. */
  end?: boolean;
  /**
   * Nombre d'éléments non lus. Zéro ou absent : aucune pastille. L'onglet est
   * le seul endroit où l'information a une chance d'être vue, l'écran
   * concerné n'étant précisément pas celui qu'on regarde.
   */
  badge?: number;
};

/**
 * Pastille rouge de l'onglet, reprise de celle de la cloche de notifications :
 * mêmes jetons, donc même contraste en thème sombre, où le rouge est clair et
 * où un blanc en dur tomberait sous le seuil AA.
 */
function NavBadge({ count, inset }: { count: number; inset: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        ...(inset
          ? { position: 'absolute', top: -4, right: -8 }
          : { marginLeft: 'auto' }),
        minWidth: 16,
        height: 16,
        padding: '0 4px',
        borderRadius: 'var(--r-full)',
        background: 'var(--red)',
        color: 'var(--on-solid)',
        font: '600 10px/16px var(--font)',
        textAlign: 'center',
        pointerEvents: 'none',
      }}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

export type AppShellBrand = {
  name: string;
  sub?: string | null;
  emblemSrc: string;
};

export type SyncConnectionState = 'online' | 'offline' | 'syncing';

/**
 * État de la synchronisation. Volontairement discret quand tout va bien : un
 * simple point vert, et une pastille explicite dès qu'il y a un problème ou des
 * modifications en attente d'envoi.
 */
export function SyncIndicator({
  state,
  pending,
  pendingNoun = 'modification(s)',
}: {
  state: SyncConnectionState;
  pending: number;
  /**
   * Nom au pluriel de ce qui est en attente. C'était la seule divergence entre
   * les deux copies de ce composant : « modification(s) » côté animateurs,
   * « action(s) » côté familles.
   */
  pendingNoun?: string;
}) {
  if (state === 'offline') {
    return (
      <Tooltip
        content={
          pending > 0
            ? `Hors connexion — ${pending} ${pendingNoun} en attente d'envoi`
            : 'Hors connexion — les données affichées viennent du cache'
        }
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Badge tone="orange" size="sm">
            <IconOffline size={12} />
            {pending > 0 ? pending : 'Hors ligne'}
          </Badge>
        </span>
      </Tooltip>
    );
  }

  if (state === 'syncing' || pending > 0) {
    return (
      <Tooltip content={`Synchronisation de ${pending} ${pendingNoun}…`}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Badge tone="accent" size="sm">
            <IconRefresh size={12} />
            Sync
          </Badge>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content="Synchronisé">
      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 4px' }}>
        <Dot tone="green" size={7} />
      </span>
    </Tooltip>
  );
}

/**
 * Menu du compte, en bas de la barre latérale et dans l'en-tête mobile.
 *
 * L'identité et les actions sont injectées : les deux applications n'ont ni le
 * même modèle d'utilisateur ni les mêmes destinations, mais elles ont le même
 * menu.
 */
export function ShellUserMenu({
  name,
  subtitle,
  avatarSrc,
  items,
  compact = false,
}: {
  name: string;
  subtitle?: string | null;
  avatarSrc?: string | null;
  items: MenuItem[];
  compact?: boolean;
}) {
  return (
    <Menu
      align={compact ? 'right' : 'left'}
      items={items}
      trigger={(props) => (
        <button
          {...props}
          type="button"
          aria-label={compact ? `Compte — ${name}` : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-3)',
            width: compact ? 'auto' : '100%',
            padding: compact ? 0 : '8px 11px',
            borderRadius: 'var(--r-md)',
            textAlign: 'left',
          }}
        >
          <Avatar name={name} src={avatarSrc} size={compact ? 30 : 32} />
          {!compact && (
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', font: 'var(--t-subhead-strong)' }} className="bala-truncate">
                {name}
              </span>
              {subtitle ? (
                <span
                  style={{ display: 'block', font: 'var(--t-caption)', color: 'var(--text-secondary)' }}
                  className="bala-truncate"
                >
                  {subtitle}
                </span>
              ) : null}
            </span>
          )}
        </button>
      )}
    />
  );
}

export function AppShell({
  brand,
  navItems,
  title,
  pathname,
  linkComponent: Link,
  sync,
  pendingNoun,
  headerActions,
  userMenu,
  compactUserMenu,
  contentMax,
  children,
  overlays,
}: {
  brand: AppShellBrand;
  navItems: AppShellNavItem[];
  /** Titre de l'écran courant, affiché dans l'en-tête mobile seulement. */
  title: string;
  /** Sert à ramener l'onglet actif dans la vue après un lien profond. */
  pathname: string;
  linkComponent: ComponentType<AppShellLinkProps>;
  sync: { state: SyncConnectionState; pending: number };
  pendingNoun?: string;
  headerActions?: ReactNode;
  userMenu: ReactNode;
  compactUserMenu?: ReactNode;
  contentMax?: string;
  children: ReactNode;
  /** Surfaces montées au niveau du shell (recherche globale, notifications). */
  overlays?: ReactNode;
}) {
  const tabbarRef = useRef<HTMLElement>(null);

  // Garde l'onglet actif visible dans la barre glissable.
  useEffect(() => {
    const active = tabbarRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    active?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [pathname]);

  return (
    <div
      className="bala-shell"
      style={contentMax ? ({ '--content-max': contentMax } as CSSProperties) : undefined}
    >
      <nav className="bala-sidebar" aria-label="Navigation principale">
        <div className="bala-brand">
          {/* Le blason de l'unité, en pleine résolution : c'est le seul endroit
              de l'interface où il est assez grand pour être reconnu. Une
              section qui téléverse son propre logo le remplace. */}
          <img className="bala-brand-emblem" src={brand.emblemSrc} alt="" width={46} height={46} />
          <div style={{ minWidth: 0 }}>
            <p className="bala-brand-name">{brand.name}</p>
            {brand.sub ? <p className="bala-brand-sub">{brand.sub}</p> : null}
          </div>
        </div>

        <ul style={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
          {navItems.map((item) => (
            <li key={item.to}>
              <Link to={item.to} end={item.end ?? item.to === '/'} className="bala-nav-link">
                {item.icon}
                {item.label}
                {item.badge ? <NavBadge count={item.badge} inset={false} /> : null}
              </Link>
            </li>
          ))}
        </ul>

        <div style={{ paddingTop: 'var(--s-3)', borderTop: '1px solid var(--separator)' }}>{userMenu}</div>
      </nav>

      {/* Filigrane sur la colonne principale uniquement : la barre latérale
          garde son chrome opaque, et les écrans de connexion n'utilisent pas
          ce composant. */}
      <div
        className="bala-main bala-watermark"
        style={{ '--app-emblem': `url(${brand.emblemSrc})` } as CSSProperties}
      >
        <header className="bala-header">
          {/* Uniquement sur mobile : sur grand écran, la barre latérale indique
              déjà où l'on est, et le titre est répété juste en dessous. */}
          <p className="bala-header-title bala-truncate">{title}</p>

          <div style={{ flex: 1 }} />

          <SyncIndicator state={sync.state} pending={sync.pending} pendingNoun={pendingNoun} />

          {headerActions}

          {/* Mobile seulement : la barre latérale est absente, il faut un accès
              au profil et à la déconnexion depuis l'en-tête. */}
          {compactUserMenu ? <div className="bala-header-user">{compactUserMenu}</div> : null}
        </header>

        <main className="bala-content">{children}</main>
      </div>

      <nav ref={tabbarRef} className="bala-tabbar" aria-label="Navigation">
        {navItems.map((item) => (
          <Link key={item.to} to={item.to} end={item.end ?? item.to === '/'} className="bala-tab">
            {/* La pastille se cale sur l'icône, pas sur l'onglet : ce dernier
                se dimensionne à son libellé, et une pastille ancrée à son bord
                aurait flotté loin du pictogramme sur les mots longs. */}
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              {item.icon}
              {item.badge ? <NavBadge count={item.badge} inset /> : null}
            </span>
            <span>{item.short}</span>
          </Link>
        ))}
      </nav>

      {overlays}
    </div>
  );
}
